import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { googleSignIn } from "../lib/googleSignIn";
import { Button } from "../components/ui/button";
import { Users, Target, Lightbulb, TrendingUp, ChevronRight, ChevronDown } from "lucide-react";
import { EvoluraLogo } from "../components/EvoluraLogo";
import { LandingVideoSection } from "../components/SwotVideo";
import SEO from "../components/SEO";
import { fbPageView } from "../utils/fbpixel";

const HERO_BG = "/hero-bg.webp";

const LandingPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    if (user && !loading) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('auth_error');
    if (error) {
      setAuthError(error);
      window.history.replaceState(null, '', '/');
    }
    fbPageView();
  }, []);

  // Override body background for dark landing page
  useEffect(() => {
    document.body.classList.add('landing-page-active');
    return () => document.body.classList.remove('landing-page-active');
  }, []);

  const handleGoogleLogin = () => {
    const inviteToken = sessionStorage.getItem('pending_invite_token') || '';
    const returnUrl = sessionStorage.getItem('pending_return_url') || '';
    googleSignIn({ inviteToken: inviteToken || undefined, returnUrl: returnUrl || undefined });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const features = [
    {
      icon: Target,
      title: "Personal SWOT",
      description: "See yourself more clearly — what's working, what's not, and what's possible.",
      tryLink: "/swot"
    },
    {
      icon: Lightbulb,
      title: "Core Values",
      description: "Name the things that actually matter to you — not what sounds good, but what's real.",
      tryLink: "/values"
    },
    {
      icon: Users,
      title: "Trusted Perspectives",
      description: "The people closest to you see things you can't. Their honesty is a gift."
    },
    {
      icon: TrendingUp,
      title: "Wheel of Life",
      description: "Rate every area of your life. See the full picture. Pick where to focus.",
      tryLink: "/wheel"
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="landing-page">
      <SEO
        path="/"
        description="Free personal SWOT analysis, core values assessment, and wheel of life tools. Get honest feedback from mentors, coworkers, family and friends to see yourself clearly."
      />

      {/* ===== HERO ZONE ===== */}
      <div
        className="relative"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 30%',
        }}
      >
        <div className="absolute inset-0 bg-black/20" />
        <div
          className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, transparent, #0a0a0f)' }}
        />

        {/* Header */}
        <header className="relative z-10">
          <div className="absolute inset-x-0 top-0 h-28 pointer-events-none" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)" }} />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-20 pt-2">
              <EvoluraLogo size={40} variant="light" />
              <Button
                onClick={handleGoogleLogin}
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/20 rounded-full px-6"
                data-testid="login-button"
              >
                Sign In
              </Button>
            </div>
          </div>
        </header>

        {/* Hero Content */}
        <section className="relative z-10 py-16 md:py-24 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-wide mb-6"
                style={{ fontFamily: 'system-ui, sans-serif', letterSpacing: '0.5px' }}
              >
                The people who know you best can help you see <span className="text-violet-300">what's possible</span>
              </h1>
              <p className="text-base sm:text-lg text-white/70 mb-8 leading-relaxed max-w-2xl">
                Some relationships change what you think is possible. Evoluras helps you build those.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Button
                  onClick={handleGoogleLogin}
                  size="lg"
                  className="bg-white text-slate-900 hover:bg-white/90 rounded-full px-8 py-3 text-base font-semibold shadow-lg shadow-white/10"
                  data-testid="get-started-button"
                >
                  Get Started
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <Link to="/wheel"
                  className="text-sm text-white/60 hover:text-white/90 transition-colors flex items-center gap-1"
                  data-testid="try-wheel-hero-link"
                >
                  Or try the Wheel of Life <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              {authError && (
                <div
                  className="mt-6 p-4 bg-red-900/40 backdrop-blur-sm border border-red-500/30 rounded-xl text-red-200 text-sm"
                  data-testid="auth-error"
                >
                  Authentication failed. Please try again.
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10" data-testid="scroll-indicator">
          <div className="flex flex-col items-center gap-1 animate-bounce">
            <span className="text-white/30 text-xs tracking-widest uppercase">Discover</span>
            <ChevronDown className="w-5 h-5 text-white/30" />
          </div>
        </div>
      </div>

      {/* ===== FEATURES ===== */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-4" style={{ fontFamily: 'system-ui, sans-serif' }}>
              Start with what's real
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Honest tools for seeing yourself clearly — with help from the people who know you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const CardTag = feature.tryLink ? Link : 'div';
              const cardProps = feature.tryLink ? { to: feature.tryLink } : {};
              return (
                <CardTag
                  key={index}
                  {...cardProps}
                  className={`block p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-300 ${feature.tryLink ? 'cursor-pointer' : ''}`}
                  data-testid={`feature-card-${index}`}
                >
                  <div className="w-12 h-12 bg-violet-500/15 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-violet-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'system-ui, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
                  {feature.tryLink && (
                    <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-violet-400">
                      Try it <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  )}
                </CardTag>
              );
            })}
          </div>

          {/* Video explainer */}
          <div className="mt-16">
            <LandingVideoSection />
          </div>
        </div>
      </section>

      {/* ===== PILLARS ===== */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-4" style={{ fontFamily: 'system-ui, sans-serif' }}>
              Four areas of who you're becoming
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              A whole-person approach to becoming who you're meant to be
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-xl bg-white/5 border-2 border-violet-500/40 backdrop-blur-sm" data-testid="pillar-myself">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-violet-400" />
                </div>
                <span className="text-xs font-medium bg-violet-500/20 text-violet-300 px-2.5 py-1 rounded-full">Available</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'system-ui, sans-serif' }}>Myself</h3>
              <p className="text-sm text-slate-400">Personal SWOT, Core Values, and more self-discovery tools</p>
            </div>
            <div className="p-6 rounded-xl bg-white/[0.03] border border-white/10 opacity-60" data-testid="pillar-dreams">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center"><Lightbulb className="w-5 h-5 text-slate-400" /></div>
                <span className="text-xs font-medium bg-white/10 text-slate-400 px-2.5 py-1 rounded-full">Upcoming</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-300 mb-2" style={{ fontFamily: 'system-ui, sans-serif' }}>My Dreams</h3>
              <p className="text-sm text-slate-500">Vision boards, goal setting, and aspiration tracking</p>
            </div>
            <div className="p-6 rounded-xl bg-white/[0.03] border border-white/10 opacity-60" data-testid="pillar-plans">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center"><Target className="w-5 h-5 text-slate-400" /></div>
                <span className="text-xs font-medium bg-white/10 text-slate-400 px-2.5 py-1 rounded-full">Upcoming</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-300 mb-2" style={{ fontFamily: 'system-ui, sans-serif' }}>My Plans</h3>
              <p className="text-sm text-slate-500">Action plans, milestones, and progress tracking</p>
            </div>
            <div className="p-6 rounded-xl bg-white/[0.03] border border-white/10 opacity-60" data-testid="pillar-habits">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center"><TrendingUp className="w-5 h-5 text-slate-400" /></div>
                <span className="text-xs font-medium bg-white/10 text-slate-400 px-2.5 py-1 rounded-full">Upcoming</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-300 mb-2" style={{ fontFamily: 'system-ui, sans-serif' }}>My Habits</h3>
              <p className="text-sm text-slate-500">Habit tracking, routines, and behavior change</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="p-12 rounded-2xl bg-gradient-to-br from-violet-600/20 to-violet-900/20 border border-violet-500/20 backdrop-blur-sm">
            <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-4" style={{ fontFamily: 'system-ui, sans-serif' }}>
              You were meant to grow with people
            </h2>
            <p className="text-violet-200/70 mb-8 text-lg">
              Sign in to start building something meaningful with the people who believe in you.
            </p>
            <Button
              onClick={handleGoogleLogin}
              size="lg"
              className="bg-white text-slate-900 hover:bg-white/90 rounded-full px-8 py-3 text-base font-semibold shadow-lg shadow-white/10"
              data-testid="cta-button"
            >
              Sign In with Google
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ===== MANIFESTO ===== */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="manifesto-section">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full opacity-[0.07]"
            style={{ background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)' }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 sm:px-8">
          {/* Section label */}
          <div className="text-center mb-16 md:mb-20">
            <span className="text-[11px] tracking-[0.3em] uppercase text-violet-400/60 font-medium">
              What we believe
            </span>
          </div>

          {/* Stanzas */}
          <div className="space-y-16 md:space-y-20 text-center">

            {/* Stanza 1 */}
            <blockquote className="relative">
              {/* Large decorative quote mark */}
              <div
                className="absolute -top-8 left-1/2 -translate-x-1/2 text-violet-500/10 select-none pointer-events-none"
                style={{ fontFamily: 'Georgia, serif', fontSize: '120px', lineHeight: '1' }}
              >
                &ldquo;
              </div>
              <p className="text-xl sm:text-2xl md:text-3xl text-white/90 font-light leading-snug tracking-wide"
                 style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
                We believe the people who change your life
                <br />
                aren't strangers on the internet.
              </p>
              <p className="text-xl sm:text-2xl md:text-3xl font-light leading-snug tracking-wide mt-3"
                 style={{ fontFamily: "'Georgia', 'Times New Roman', serif", color: '#a78bfa' }}>
                They're the ones who've seen you at your worst
                <br />
                and still believe in your best.
              </p>
            </blockquote>

            {/* Divider */}
            <div className="flex justify-center">
              <div className="w-12 h-px bg-violet-500/25" />
            </div>

            {/* Stanza 2 */}
            <blockquote>
              <p className="text-xl sm:text-2xl md:text-3xl text-white/90 font-light leading-snug tracking-wide"
                 style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
                Growth isn't a solo sport.
              </p>
              <p className="text-xl sm:text-2xl md:text-3xl text-white/50 font-light leading-snug tracking-wide mt-3"
                 style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
                The strongest people we know
                <br />
                didn't figure it out alone —
              </p>
              <p className="text-xl sm:text-2xl md:text-3xl font-light leading-snug tracking-wide mt-3"
                 style={{ fontFamily: "'Georgia', 'Times New Roman', serif", color: '#a78bfa' }}>
                they were honest enough to ask,
                <br />
                and brave enough to listen.
              </p>
            </blockquote>

            {/* Divider */}
            <div className="flex justify-center">
              <div className="w-12 h-px bg-violet-500/25" />
            </div>

            {/* Stanza 3 */}
            <blockquote>
              <p className="text-xl sm:text-2xl md:text-3xl text-white/90 font-light leading-snug tracking-wide"
                 style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
                Evoluras exists for that moment
                <br />
                when someone you trust
              </p>
              <p className="text-xl sm:text-2xl md:text-3xl font-light leading-snug tracking-wide mt-3"
                 style={{ fontFamily: "'Georgia', 'Times New Roman', serif", color: '#a78bfa' }}>
                says the thing you needed to hear
                <br />
                but couldn't tell yourself.
              </p>
            </blockquote>
          </div>
        </div>
      </section>

      {/* ===== FAQ Section (SEO) ===== */}
      <section className="py-16 relative" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-semibold text-white mb-8 text-center" style={{ fontFamily: 'system-ui, sans-serif' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {[
              {
                q: "What is a personal SWOT analysis?",
                a: "A personal SWOT analysis is a self-assessment framework that helps you identify your Strengths, Weaknesses, Opportunities, and Threats. Unlike a business SWOT, it focuses on you as an individual — your skills, habits, blind spots, and the external factors that affect your growth."
              },
              {
                q: "How does Evoluras help with personal development?",
                a: "Evoluras gives you structured self-reflection tools (SWOT, Core Values, Wheel of Life) and then lets you invite the people who know you best — mentors, friends, coworkers, family — to share their honest perspective. Combining self-awareness with outside feedback creates a clearer picture of who you are."
              },
              {
                q: "What is a core values assessment?",
                a: "A core values assessment helps you identify the fundamental beliefs and principles that guide your decisions. Evoluras provides 50 curated values and 50 anti-values, organized in a visual pyramid that makes it easy to prioritize what matters most to you."
              },
              {
                q: "What is the Wheel of Life tool?",
                a: "The Wheel of Life is a visual coaching tool where you rate 10 areas of your life (career, health, relationships, etc.) on a scale of 1-10. It reveals which areas are thriving and which need attention, helping you decide where to focus your energy."
              },
              {
                q: "Is Evoluras free to use?",
                a: "Yes. All tools — Personal SWOT, Core Values, and Wheel of Life — are available for free. You can try them as a guest without signing up, and create an account when you're ready to save your progress and invite your network."
              },
            ].map((item, i) => (
              <details key={i} className="group rounded-xl border border-white/10 bg-white/5 overflow-hidden">
                <summary className="flex items-center justify-between p-4 cursor-pointer text-sm font-medium text-white hover:bg-white/[0.05] transition-colors">
                  {item.q}
                  <ChevronDown className="w-4 h-4 text-slate-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
                </summary>
                <p className="px-4 pb-4 text-sm text-slate-400 leading-relaxed">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Evoluras",
          "url": "https://evoluras.com",
          "description": "Free personal SWOT analysis, core values assessment, and wheel of life tools with feedback from your trusted network.",
          "applicationCategory": "LifestyleApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "120"
          }
        })}}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "What is a personal SWOT analysis?", "acceptedAnswer": { "@type": "Answer", "text": "A personal SWOT analysis is a self-assessment framework that helps you identify your Strengths, Weaknesses, Opportunities, and Threats. Unlike a business SWOT, it focuses on you as an individual." }},
            { "@type": "Question", "name": "What is a core values assessment?", "acceptedAnswer": { "@type": "Answer", "text": "A core values assessment helps you identify the fundamental beliefs and principles that guide your decisions. Evoluras provides 50 curated values and 50 anti-values in a visual pyramid." }},
            { "@type": "Question", "name": "What is the Wheel of Life tool?", "acceptedAnswer": { "@type": "Answer", "text": "The Wheel of Life is a visual coaching tool where you rate 10 areas of your life on a scale of 1-10, revealing which areas need attention." }},
            { "@type": "Question", "name": "Is Evoluras free?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. All tools are available for free. You can try them as a guest and create an account to save your progress." }}
          ]
        })}}
      />

      {/* Footer */}
      <footer className="border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <EvoluraLogo size={36} variant="light" />
            <div className="flex items-center gap-5 text-sm">
              <Link to="/privacy" className="text-slate-400 hover:text-slate-200 transition-colors" data-testid="footer-privacy-link">
                Privacy
              </Link>
              <Link to="/sacsaae" className="text-slate-400 hover:text-slate-200 transition-colors" data-testid="footer-csae-link">
                Child Safety
              </Link>
              <p className="text-slate-500">
                &copy; 2026 Evoluras. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
